import React from 'react';
import { duoPermissions, duoModifiers, duoAdminModifiers } from '../data/duo';
import StepHero from './StepHero';

export default function StepConsent({ inputs, onChange, onToggleModifier, glowField }) {
  const selected = new Set(inputs.duoModifiers || []);
  const needsPermission = glowField === 'duoPermission';
  return (
    <section data-testid="step-consent">
      <StepHero
        sectionLabel="GA4GH DUO consent"
        title={<>What can buyers <em style={emStyle}>actually do</em> with the data?</>}
        lede={'Pick the broadest GA4GH Data Use Ontology code your consent forms support, then add any modifiers. Broader consent unlocks more buyer types (commercial pharma, AI training); modifiers like "Non-commercial only" eliminate entire revenue streams.'}
      />

      <div style={{ marginTop: '32px' }}>
        <div style={fieldLabelStyle}>Primary permission code</div>
        <div style={needsPermission ? duoGroupGlowStyle : duoGroupStyle}>
        <div style={duoGridStyle}>
          {duoPermissions.map((p) => {
            const isSel = inputs.duoPermission === p.value;
            return (
              <ClickableRow
                key={p.value}
                onActivate={() => onChange('duoPermission', p.value)}
                style={isSel ? duoCardOnStyle : duoCardStyle}
                role="radio"
                ariaChecked={isSel}
              >
                <span style={isSel ? duoRadioOnStyle : duoRadioStyle} aria-hidden="true" />
                <div>
                  <div>
                    <span style={duoNameStyle}>{p.label.replace(/ \(DUO:.*\)$/, '')}</span>
                    <span style={duoCodeStyle}>{p.value} · {extractCode(p.label)}</span>
                    {isSel && <span style={selectedTagStyle}>· selected</span>}
                  </div>
                  <div style={duoDescStyle}>{p.tip}</div>
                </div>
                <div style={duoScoreColStyle}>
                  <div style={duoScoreStyle}>{p.score}</div>
                  <div style={duoScoreLabelStyle}>score</div>
                </div>
              </ClickableRow>
            );
          })}
        </div>
        </div>
      </div>

      <div style={{ marginTop: '32px' }}>
        <div style={fieldLabelStyle}>Modifiers · check all that apply</div>
        <div style={modGridStyle}>
          {[...duoModifiers, ...duoAdminModifiers].map((m) => {
            const isOn = selected.has(m.value);
            return (
              <ClickableRow
                key={m.value}
                onActivate={() => onToggleModifier(m.value)}
                style={isOn ? modCardOnStyle : modCardStyle}
                role="checkbox"
                ariaChecked={isOn}
              >
                <span style={isOn ? modCheckOnStyle : modCheckStyle} aria-hidden="true">
                  {isOn && (
                    <svg width="10" height="10" viewBox="0 0 24 24">
                      <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <div style={modTextStyle}>
                  {m.label} <span style={modCodeStyle}>{m.value}</span>
                </div>
                <div style={m.adjustment === 0 ? modAdjNeutralStyle : (isOn ? modAdjOnStyle : modAdjStyle)}>
                  {m.adjustment === 0 ? '0 · admin' : m.adjustment}
                </div>
              </ClickableRow>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ClickableRow is a <div role="radio|checkbox"> with keyboard activation,
// used in place of <button> for the DUO permission and modifier rows.
// Going through a non-<button> sidesteps a UA-drawn dark focus border
// some browsers leave on previously-clicked buttons that we couldn't
// reset via outline / box-shadow / appearance CSS overrides.
function ClickableRow({ role, ariaChecked, onActivate, style, children }) {
  function onKeyDown(e) {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onActivate();
    }
  }
  return (
    <div
      role={role}
      tabIndex={0}
      aria-checked={ariaChecked}
      onClick={onActivate}
      onKeyDown={onKeyDown}
      style={style}
    >
      {children}
    </div>
  );
}

function extractCode(label) {
  const match = label.match(/\(DUO:[^)]+\)/);
  return match ? match[0].slice(1, -1) : '';
}

const emStyle = { fontStyle: 'italic', color: 'var(--accent)' };
const fieldLabelStyle = { fontSize: '12px', color: 'var(--muted)', marginBottom: '10px' };

const duoGridStyle = { display: 'grid', gap: '10px' };
const duoGroupStyle = {
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderRadius: '8px',
  padding: '6px',
  margin: '-6px',
};
const duoGroupGlowStyle = {
  ...duoGroupStyle,
  borderColor: 'var(--amber)',
  boxShadow: '0 0 0 4px rgba(245, 158, 11, 0.18)',
};
// React strips both shorthand and longhand border-* values when they're
// mixed in the same style object (spreading a base with `border: …` then
// overriding `borderColor: …` triggers the conflict). That left every
// border-* property empty in the DOM, which is what was actually drawing
// the "dark border" in StepConsent — not focus chrome. Using longhands
// throughout sidesteps the conflict.
const duoCardBase = {
  width: '100%',
  padding: '16px 18px',
  background: 'var(--surface)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--line)',
  borderRadius: '4px',
  cursor: 'pointer',
  display: 'grid',
  gridTemplateColumns: '26px 1fr 80px',
  gap: '14px',
  alignItems: 'start',
  textAlign: 'left',
  fontFamily: 'var(--font)',
  color: 'var(--ink)',
};
const duoCardStyle = duoCardBase;
const duoCardOnStyle = { ...duoCardBase, borderColor: 'var(--accent)', background: 'var(--accent-bg)', boxShadow: 'inset 0 0 0 1px var(--accent)' };

const duoRadioBase = { width: '18px', height: '18px', borderWidth: '2px', borderStyle: 'solid', borderColor: 'var(--line)', borderRadius: '50%', marginTop: '2px' };
const duoRadioStyle = duoRadioBase;
const duoRadioOnStyle = { ...duoRadioBase, borderColor: 'var(--accent)', boxShadow: 'inset 0 0 0 4px var(--accent)' };

const duoNameStyle = { fontSize: '14px', fontWeight: 500, color: 'var(--ink)' };
const duoCodeStyle = { fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--muted)', marginLeft: '6px' };
const selectedTagStyle = { fontSize: '11px', color: 'var(--accent)', marginLeft: '6px', fontWeight: 500 };
const duoDescStyle = { fontSize: '12.5px', color: 'var(--ink2)', marginTop: '4px', lineHeight: 1.5 };
const duoScoreColStyle = { textAlign: 'right' };
const duoScoreStyle = { fontFamily: 'var(--display)', fontStyle: 'italic', fontSize: '24px', color: 'var(--accent)', fontWeight: 500, lineHeight: 1 };
const duoScoreLabelStyle = { fontSize: '10.5px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '4px' };

const modGridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' };
const modCardBase = {
  padding: '12px 14px',
  background: 'var(--surface)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--line)',
  borderRadius: '4px',
  cursor: 'pointer',
  display: 'grid',
  gridTemplateColumns: '18px 1fr 70px',
  gap: '12px',
  alignItems: 'center',
  textAlign: 'left',
  fontFamily: 'var(--font)',
  color: 'var(--ink)',
};
const modCardStyle = modCardBase;
const modCardOnStyle = { ...modCardBase, borderColor: 'var(--warm)', background: 'var(--warm-bg)' };

const modCheckBase = { width: '16px', height: '16px', borderWidth: '1.5px', borderStyle: 'solid', borderColor: 'var(--line)', borderRadius: '3px', display: 'grid', placeItems: 'center' };
const modCheckStyle = modCheckBase;
const modCheckOnStyle = { ...modCheckBase, background: 'var(--warm)', borderColor: 'var(--warm)' };

const modTextStyle = { fontSize: '12.5px', color: 'var(--ink)' };
const modCodeStyle = { fontFamily: 'var(--mono)', fontSize: '10.5px', color: 'var(--muted)', marginLeft: '4px' };
const modAdjStyle = { fontVariantNumeric: 'tabular-nums', fontSize: '12.5px', textAlign: 'right', color: 'var(--muted)' };
const modAdjOnStyle = { ...modAdjStyle, color: 'var(--warm)', fontWeight: 500 };
const modAdjNeutralStyle = { ...modAdjStyle, fontSize: '11px' };
